import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAppFeatures } from './AppFeaturesContext';
import { useAuth } from './AuthContext';
import authService from '../services/auth.service';
import { getVersionOutdated } from '../utils/versionOutdated';
import { useNavigate } from 'react-router-dom';
import { showToast } from '../utils/toast';
import watchdogWebSocketService from '../services/watchdogWebSocket.service';
import type { DashboardUpdateData, WorkSessionAlert } from '../types/watchdog.types';

interface WatchdogContextValue {
  dashboard: DashboardUpdateData | null;
  recentAlerts: WorkSessionAlert[];
  connected: boolean;
  lastUpdate: string | null;
}

const WatchdogContext = createContext<WatchdogContextValue | undefined>(undefined);

const MAX_RECENT_ALERTS = 20;

export const WatchdogProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [dashboard, setDashboard] = useState<DashboardUpdateData | null>(null);
  const [recentAlerts, setRecentAlerts] = useState<WorkSessionAlert[]>([]);
  const [connected, setConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);

  const { shift } = useAppFeatures();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const canConnect = shift && profile;

  const handleDashboard = useCallback((data: DashboardUpdateData, timestamp: string) => {
    setDashboard(data);
    setLastUpdate(timestamp);
    if (data.recent_alerts) {
      setRecentAlerts(data.recent_alerts.slice(0, MAX_RECENT_ALERTS));
    }
  }, []);

  useEffect(() => {
    if (!canConnect) {
      watchdogWebSocketService.disconnect();
      setConnected(false);
      setDashboard(null);
      setRecentAlerts([]);
      setLastUpdate(null);
      return;
    }

    if (getVersionOutdated()) return;
    const tokens = authService.getTokens();
    if (!tokens?.access_token) return;

    const unsubDashboard = watchdogWebSocketService.onDashboardUpdate(handleDashboard);
    const unsubConn = watchdogWebSocketService.onConnectionChange(setConnected);
    const unsubSession = watchdogWebSocketService.onSessionEvent((event) => {
      if (event === 'session_invalidated') {
        showToast('Ваша сессия была завершена', 'warning');
        navigateRef.current('/login');
      }
    });

    watchdogWebSocketService.connect();

    const handleVisibility = () => {
      if (!document.hidden && !watchdogWebSocketService.isConnected()) {
        if (!getVersionOutdated() && authService.getTokens()?.access_token) {
          watchdogWebSocketService.connect();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      unsubDashboard();
      unsubConn();
      unsubSession();
      watchdogWebSocketService.disconnect();
    };
  }, [canConnect, handleDashboard]);

  const value = useMemo<WatchdogContextValue>(
    () => ({
      dashboard,
      recentAlerts,
      connected,
      lastUpdate,
    }),
    [dashboard, recentAlerts, connected, lastUpdate]
  );

  return <WatchdogContext.Provider value={value}>{children}</WatchdogContext.Provider>;
};

export const useWatchdog = (): WatchdogContextValue => {
  const ctx = useContext(WatchdogContext);
  if (!ctx) throw new Error('useWatchdog must be used within WatchdogProvider');
  return ctx;
};
